import { Divider, Typography } from "@mui/material";
import { Select as AntSelect, Spin, Modal } from "antd";
import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import {
  showSuccessNotification,
  showErrorNotification,
} from "src/util/notifications";
import Breadcrumb from "src/components/common/Breadcrumb/Breadcrumb";
import Button from "src/components/common/Button/Button";
import TableFilter from "src/components/common/TableFilter/TableFilter";
import dayjs from "dayjs";
import Label from "src/components/common/Label/Label";
import { fetchInventoryPostLocationsRequest } from "../UndoInventoryPost/action";
import { undoInventoryPost } from "./saga";
import { useFeaturePermission } from "src/context/FeaturePermissionContext";
import { FEATURE_NAMES } from "src/util/constants/feature.constants";

const { Option } = AntSelect;

function UndoInventoryPost() {
  const dispatch = useDispatch();
  const { accounts }: any = useMsal();
  const { isReadOnly } = useFeaturePermission(FEATURE_NAMES.UndoInventoryPost);
  const [loader, setLoader] = useState(false);
  const todayIso = new Date().toISOString().split("T")[0];
  const formattedDate = dayjs(todayIso).format("MM/DD/YYYY");
  const didFetchRef = useRef(false);

  // selector shape: state.inventoryLocations.list
  const locations = useSelector(
    (state: any) => state.UNDO_INVENTORY_POST_STATE_KEY?.data || []
  );
  const loading = useSelector(
    (state: any) => state.UNDO_INVENTORY_POST_STATE_KEY?.loading
  );
  const [selectedLocations, setSelectedLocations] = useState<string[] | string>(
    []
  );

  // unique list of OfficeNum values for first dropdown
  const officeList = useMemo<string[]>(() => {
    // collect OfficeNum values, dedupe and return as strings
    return Array.from(
      new Set((locations || []).map((l: any) => l?.OfficeNum).filter(Boolean))
    ).map(String) as string[];
  }, [locations]);

  // second dropdown: IDs for selected OfficeNum
  const [selectedOffice, setSelectedOffice] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const idsForSelectedOffice = useMemo(() => {
    if (!selectedOffice) return [];
    return (locations || [])
      .filter((l: any) => l?.OfficeNum === selectedOffice)
      .map((l: any) => l?.ID)
      .filter((id: any) => id !== undefined && id !== null);
  }, [locations, selectedOffice]);

  useEffect(() => {
    if (!didFetchRef.current) {
      dispatch(fetchInventoryPostLocationsRequest());
      didFetchRef.current = true;
    }
  }, [dispatch]);

  function changePicker(value: string) {
    setSelectedOffice(value);
    setSelectedId(null);
    // keep existing selection state if you need array behaviour:
    setSelectedLocations(value);
  }

const handleApiCall = async (apiFunction: Function, payload: any, onSuccess?: Function) => {
    try {
      setConfirmLoading(true);
      const res = await apiFunction(payload);
      if (onSuccess) onSuccess(res);
    } catch (error: any) {
      showErrorNotification(error?.response?.data?.error?.messages?.[0] || "An error occurred");
    } finally {
      setConfirmLoading(false);
    }
  };


  const [saveModal, setSaveModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleUndoInventoryPost = async () => {
    if (!selectedId) return;

    const payload = {
      InventoryPhysicalId: selectedId,
      Username: accounts?.[0]?.username || "",
    };

    await handleApiCall(undoInventoryPost, payload, async (res: any) => {
      const lower = (typeof res === "string" ? res.toLowerCase() : "");
      if (lower.includes("success")) {
        showSuccessNotification(`Reverted the posted inventory ${selectedId} successfully.`);
      } else if (res) {
        showErrorNotification(res);
      }
    });
    setSelectedOffice("");
    setSelectedId(null);
    dispatch(fetchInventoryPostLocationsRequest());
    setSaveModal(false);
  };

  return (
    <>
      <Spin spinning={loader || loading}>
        <div>
          <Divider />
          <Breadcrumb>
            <span>Undo Inventory Post</span>
          </Breadcrumb>
          <TableFilter>
            <div
              className="form align-items-start"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              <Label
                label={"Current Date: " + formattedDate}
                className="flex-row align-items-center fs-6 gap-3"
                style={{ marginTop: 3 }}
                children={undefined}
              />

              <AntSelect
                placeholder="Select Location"
                disabled={loader}
                showSearch
                style={{ width: 160 }}
                optionFilterProp="children"
                onChange={(value: any) => changePicker(value)}
                value={selectedOffice || undefined}
                allowClear
              >
                {officeList.map((office: string) => (
                  <Option key={office} value={office}>
                    {office}
                  </Option>
                ))}
              </AntSelect>

              <AntSelect
                placeholder="Select Physical ID"
                disabled={!selectedOffice || loader}
                style={{ width: 160 }}
                value={selectedId ?? undefined}
                onChange={(val: any) => setSelectedId(Number(val))}
                allowClear
                notFoundContent={
                  loading ? <Spin size="small" /> : "No IDs available"
                }
              >
                {idsForSelectedOffice.map((id: any) => (
                  <Option key={id} value={id}>
                    {id}
                  </Option>
                ))}
              </AntSelect>

              <Button
                hidden={isReadOnly}
                disabled={!(selectedOffice && selectedId) || loader}
                onClick={() => setSaveModal(true)}
                size="middle"
              >
                Undo Post
              </Button>
            </div>
          </TableFilter>

          {/* show centered message when officeList is empty and not loading */}
          {!loading && (!officeList || officeList.length === 0) && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 220,
                width: "100%",
              }}
            >
              <Typography variant="h6">
                There are no locations with posted inventory
              </Typography>
            </div>
          )}
        </div>
      </Spin>

      <Modal
        title="Confirm"
        okText="Ok"
        centered
        confirmLoading={confirmLoading}
        style={{ top: 20 }}
        open={saveModal}
        onOk={handleUndoInventoryPost}
        onCancel={() => setSaveModal(false)}
      >
        <span>
          Are you sure you wish to reverse the posted inventory for this
          location
        </span>
      </Modal>
    </>
  );
}
export default UndoInventoryPost;
